/**
 * PromptBuilder — constructs AI prompts for SPB Analytics summaries.
 *
 * Ported from docs/reference/processForFinn/prompt_generator.py.
 * Produces two prompts: marketplace analysis and trend analysis.
 * These are sent via submitQuestion → poll getConversation.
 */

/** Metadata shape matching the analysis context. */
export interface PromptMetadata {
  product: string;
  targetDateStart: string;
  targetDateEnd: string;
  baselineDateStart: string;
  baselineDateEnd: string;
  comparisonMode: string;
}

// ---------------------------------------------------------------------------
// Shared prompt blocks
// ---------------------------------------------------------------------------

const IMPORTANT_CONTEXT = (meta: PromptMetadata) => `
<IMPORTANT>
  NEVER run Athena query. NEVER run Metric Analyzer. Never get Knowledge. This is important.
  Just analyze YoY Revenue on ${meta.targetDateStart}~${meta.targetDateEnd}(target) compared to ${meta.baselineDateStart}~${meta.baselineDateEnd}(baseline) using only the data provided without making any external API or database calls.
  Do not copy or paraphrase any sentences from this <IMPORTANT> block in the output; these constraints are for internal use only and must not be mentioned or echoed back to the user.
</IMPORTANT>
<no_artifacts_guard>
  Output narrative only.
  - Do NOT output JSON or any structured artifacts.
  - Do NOT output or mention any of these tokens/phrases: "SEARCH_SPLIT", "VALIDATION", "FAIL", "STOP", "RECOMPUTE", "math_gate", "self-check", "recalculate", "cannot proceed".
  - Do NOT describe missing granularity or inability to validate. Use available values as-is.
</no_artifacts_guard>
`;

const FORMAT_BLOCK = (meta: PromptMetadata) => `
<format>
  * Global rules for the <report_output>:
    ** Use neutral, fact-based tone: do not use subjective language (e.g., "significant", "strong", "remarkable", "substantial"). Avoid speculation or interpretation.
    ** Do not include formulas or metric definitions (e.g., no need to spell out or explain RPOM).
    ** Always use Mobile (not Smartphone).
    ** Replace all mentions of Ad Density with Ad Count.
    ** No need to include the framework formula in the output.
    ** Do not attribute growth to external factors you don't have data for (e.g., "broader economic fluctuations").
    ** When referring to forecasted (RO or OP2) traffic or revenue, use guidance revenue/traffic or OP2 revenue/traffic.
    ** Do NOT restate the task, constraints, or data sources in the output. Never include phrases like "I understand you want me to analyze...", "I'll analyze this", "using only the data provided", "without making any external API or database calls", or any mention of "trend_data", "forecast_traffic_data", "holiday_list", "Athena", "API", "database", "instructions", or "constraints". Start directly with the analytical findings.
    ** If your draft begins with any meta-commentary such as "I understand you want me to analyze...", "Let me analyze...", "using only the provided data", or similar task restatements, remove those sentences and start directly with the revenue analysis (e.g., "US Desktop+Mobile revenue between X and Y was $ZMM, showing a +Y% WoW change.").
  * Numerical formatting:
    ** Use commas for large numbers (e.g., 1,204MM)
    ** Round percentages to 1 decimal place (e.g., 87.8%)
    ** Use currency symbols "$" for Revenue and RPOM.
    ** Use "MM" for millions (e.g., $56.9MM), "B" for billions (e.g., $56.9B).
  * Executive Summary formatting:
    ** Present content in paragraphs, not lists or breakout sections.
    ** Never emit tabular markup (no use of <table>, <thead>, <tbody>, <tr>, <td>, <th>, or Markdown tables). Convert any tabular idea into full sentences before responding.
    ** If there are more than one dates, always lead with the later dates.
    ** Formatting Rules for Growth Metric (e.g., YoY, MoM, WoW, DoD)
    *** If you need to include both actual number ($X) and period-over-period (+Y% PoP), please put actual number first.
    **** Example: Revenue between X and Y was $XMM, showing a +Y% PoP (+$ZMM), or
    **** On X, Page Views rose to $XM (+$ZM, +Y% PoP)
</format>
`;

const HTML_OUTPUT_FORMATTING = `
<html_output_formatting>
  ** Output raw HTML content directly without code fences or formatting markers - no introductory text and no need to include explanations about what the HTML report is based on.
  ** CRITICAL: Do not wrap the output in code fences or any other markdown formatting.
  ** The response should start immediately with HTML content, not with code fence markers.
  ** Output the HTML directly as if it will be rendered, not displayed as source code.
  ** Coloring Rules:
    **** For every positive change (e.g., "+9.5% WoW", "+$5.7MM"), wrap only the numeric/delta portion in an inline element with green text, such as: <span style="color:#00c853">+9.5% WoW</span>.
    **** For every negative change (e.g., "-3.8%", "-$2.1MM"), wrap only the numeric/delta portion in an inline element with red text, such as: <span style="color:#ff5252">-3.8%</span>.
    **** Apply these coloring rules consistently across the entire summary, including the opening executive-summary sentences, not just the breakdown sections.
</html_output_formatting>
`;

const HOLIDAY_LIST = `
geo,holiday,start_date,end_date
AE,Prime Day,2024-07-16,2024-07-17
AU,Prime Day,2024-07-16,2024-07-17
BR,Prime Day,2024-07-16,2024-07-17
CA,Prime Day,2024-07-16,2024-07-17
DE,Prime Day,2024-07-16,2024-07-17
ES,Prime Day,2024-07-16,2024-07-17
FR,Prime Day,2024-07-16,2024-07-17
IN,Prime Day,2024-07-20,2024-07-21
IN,Great Freedom Festival,2024-08-06,2024-08-11
IT,Prime Day,2024-07-16,2024-07-17
JP,Prime Day,2024-07-16,2024-07-17
MX,Prime Day,2024-07-16,2024-07-17
NL,Prime Day,2024-07-16,2024-07-17
SA,Prime Day,2024-07-16,2024-07-17
UK,Prime Day,2024-07-16,2024-07-17
US,Prime Day,2024-07-16,2024-07-17
TR,Prime Day,2024-07-16,2024-07-17
BE,Prime Day,2024-07-16,2024-07-17
SG,Prime Day,2024-07-16,2024-07-17
SE,Prime Day,2024-07-16,2024-07-17
PL,Prime Day,2024-07-16,2024-07-17
EG,Prime Day,2024-07-16,2024-07-17
AE,Prime Day,2025-07-08,2025-07-11
AU,Prime Day,2025-07-08,2025-07-11
BR,Prime Day,2025-07-08,2025-07-11
CA,Prime Day,2025-07-08,2025-07-11
DE,Prime Day,2025-07-08,2025-07-11
ES,Prime Day,2025-07-08,2025-07-11
FR,Prime Day,2025-07-08,2025-07-11
IN,Prime Day,2025-07-12,2025-07-14
IN,Great Freedom Festival,2025-07-31,2025-08-07
IT,Prime Day,2025-07-08,2025-07-11
JP,Prime Day,2025-07-08,2025-07-11
MX,Prime Day,2025-07-09,2025-07-15
NL,Prime Day,2025-07-08,2025-07-11
SA,Prime Day,2025-07-08,2025-07-11
UK,Prime Day,2025-07-08,2025-07-11
US,Prime Day,2025-07-08,2025-07-11
TR,Prime Day,2025-07-08,2025-07-11
BE,Prime Day,2025-07-08,2025-07-11
SG,Prime Day,2025-07-08,2025-07-11
SE,Prime Day,2025-07-08,2025-07-11
PL,Prime Day,2025-07-08,2025-07-11
EG,Prime Day,2025-07-08,2025-07-11
`;

function getBusinessFramework(product: string, geoGroupName: string): string {
  if (product === "SB") {
    return `
    <business_framework>
    Here provides a detailed business granularity knowledge.
    * Geo Hierarchy:
      ** Three levels: WW → Region → Marketplace
      ** WW (Worldwide) = NA + EU + ROW
      ** Three Regions (NA, EU, ROW) & Marketplaces
        *** NA: US, CA, MX
        *** EU: UK, DE, FR, IT, ES, NL, SE, PL, BE, IE
        *** ROW: JP, IN, AU, AE, KSA, BR, SG, TR, ZA
    * Dateframe Hierarchy:
      ** H1 = first half of the year (Q1 + Q2, Jan–Jun); H2 = second half (Q3 + Q4, Jul–Dec)
      ** Quarter = three months (e.g., Q1 = Jan-Mar)
      ** Month = the first day to the last day of the same month
      ** Week = start with a Sunday and end with a Saturday
    * Page Types:
      ** Two Page Types: 'Search' and 'Detail' (and an aggregate 'Search+Detail')
      ** SB Search may include aggregates such as Total Sparkle and/or Page 1 / Page 2+ totals.
      ** Treat all provided SB columns as authoritative; do not attempt to reconcile rollups.
    * Device Type:
      *** Desktop, Mobile (or Desktop+Mobile)
    * Revenue analysis framing:
      ** Revenue can be described via Traffic and RPOM (do not include formulas).
      ** RPOM can be described via Coverage, Ad CTR, Ad Count, CPC (use provided changes; no definitions).
    </business_framework>`;
  }

  return `
    <business_framework>
    Here provides a detailed business granularity knowledge.
    * Geo Hierarchy:
      ** Three levels: WW → Region → Marketplace
      ** WW (Worldwide) = NA + EU + ROW
      ** Three Regions (NA, EU, ROW) & Marketplaces
        *** NA: US, CA, MX
        *** EU: UK, DE, FR, IT, ES, NL, SE, PL, BE, IE
        *** ROW: JP, IN, AU, AE, KSA, BR, SG, TR, ZA
    * Dateframe Hierarchy:
      ** H1 = first half of the year (Q1 + Q2, Jan–Jun); H2 = second half (Q3 + Q4, Jul–Dec)
      ** Quarter = three months (e.g., Q1 = Jan-Mar)
      ** Month = the first day to the last day of the same month
      ** Week = start with a Sunday and end with a Saturday
    * Page Type, Page Number and Placement:
      ** Two Page Types: 'Search' and 'Detail'.
      ** Hierarchy:
        *** Search Page can be split into Page 1 and Page 2+ (Page 2 and beyond). Both include two placement types:
          **** Native placements: TOP, MOP, BOP.
          **** Widget placements: Search Thematic (includes Inline, Bottom).
      ** Detail Page:
        *** DP1, DP2, DP Thematic, HQP, ILM.
    * Device Type:
      *** Desktop
      *** Mobile
    * Revenue analysis framework of the output:
      ** 1st layer: Revenue is a function of both Page Views and RPOM. (Ad Revenue = RPOM * Page views / 1000). Page Views can be considered as Traffic.
      ** 2nd layer: RPOM can be explained by 4 major components. (RPOM = Coverage * Ad CTR * Ad Count * CPC)
    </business_framework>`;
}

function getSBGuards(isSB: boolean): string {
  if (!isSB) return "";
  return `
    <sb_hard_no_validation>
    This request is for Sponsored Brands (SB/HSA).
    - Do NOT perform or describe any validation, reconciliation, self-checks, or recomputation.
    - Do NOT output JSON, "SEARCH_SPLIT", "VALIDATION", "FAIL", "STOP", "RECOMPUTE", or any debug artifacts.
    - Output narrative only, using the provided values as-is even if rollups do not align.
    </sb_hard_no_validation>
    <sb_data_guidance>
    For Sponsored Brands, columns may include aggregates (e.g., Search-Total_Sparkle or combined P1/P2).
    Do NOT try to reconcile Page 1 vs Page 2+ sums or validate placement rollups.
    Use the provided values as-is and proceed to narrative even if totals and placements do not align.
    </sb_data_guidance>`;
}

function getSBOutputGuard(isSB: boolean): string {
  if (!isSB) return "";
  return `
    <sb_output_guard>
    For Sponsored Brands (SB/HSA):
    - Output narrative only (no debug sections, no self-checks, no preambles, no structured artifacts).
    - If a requested breakdown is not available (e.g., Page 1 vs Page 2+ or placement rollups), write the narrative using the available totals instead.
    </sb_output_guard>`;
}

function getPageGroupingRules(isSB: boolean): string {
  if (isSB) return "";
  return `
    * Page grouping rules (product-specific):
      ** For Sponsored Products (SP) only:
        NO other columns may be included. NO reallocation. NO averaging.
        *** SP Page 1 revenue must equal the sum of exactly these 5 columns:
          - Search-Page1-Native-TOP
          - Search-Page1-Native-MOP
          - Search-Page1-Native-BOP
          - Search-Page1-Widget-Inline
          - Search-Page1-Widget-Bottom
        *** SP Page 2+ revenue must equal the sum of exactly these 5 columns:
          - Search-Page2-Native-TOP
          - Search-Page2-Native-MOP
          - Search-Page2-Native-BOP
          - Search-Page2-Widget-Inline
          - Search-Page2-Widget-Bottom`;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Build the marketplace analysis prompt.
 *
 * @param analysisData   - Stringified report data (finn_analysis_txt)
 * @param metadata       - Analysis metadata (dates, product, etc.)
 * @param forecastData   - Stringified forecast traffic data
 * @param geoName        - Geo group name (e.g. 'WW', 'US')
 * @param device         - Device type (e.g. 'Desktop+Mobile')
 */
export function buildMarketplaceAnalysisPrompt(
  analysisData: string,
  metadata: PromptMetadata,
  forecastData: string,
  geoName: string,
  device: string
): string {
  const isSB = metadata.product === "SB";
  const product = metadata.product;

  const sbReportOutputBody = `
    * SB output contract (apply ONLY for SB/HSA):
      ** Output narrative ONLY for each geo level found in finn_analysis_txt in this order:
         1) WW (if present), 2) each Region (NA/EU/ROW) present, 3) each Marketplace present.
      ** For EACH geo, write exactly one paragraph that covers:
         - Revenue level for the target period and PoP % change (from Metrics + Metric Changes).
         - Whether Search vs Detail contributed more to the revenue change (from Metrics Contribution / Metrics Bridge).
         - Drivers for Search and Detail via Traffic vs RPOM (do not compare across page types).
         - RPOM component contributors (Coverage, Ad CTR, Ad Count, CPC) for the page type that contributed most (use available totals; no reconciliation).
      ** If any breakdown is missing, omit it and proceed with available totals.
      ** Never emit any validation/reconciliation/debug content; do not mention missing granularity or inability to validate.`;

  const spReportOutputBody = `
    * SP Device Summary Analysis (output 4 separated paragraphs without titles, fixed workflow)
      ** ${geoName} ${device} Revenue
        *** Report ${product} ${device} revenue for the target period from the finn_analysis_txt.
        *** Report the Metrics Changes(%) change.
        *** Refer to holiday_data_context to check if the target and baseline period fall within a holiday using the call-out rule.
        *** Do not include any interpretation on the % change.
        *** Based on the % change reported in the prior step, refer to the Contribution table in finn_analysis_txt and bridge it to the next paragraph by stating how each Page Type contribute to the change.
      ** Revenue Page Type Breakdown - refer to finn_analysis_txt.
        *** Data Sources
            * Revenue, RPOM, Coverage, Ad CTR, Ad Count, CPC, traffic only from finn_analysis_txt.
            * Forecast traffic (ro_delta, ro_variance, op_delta, op_variance) only from forecast_traffic_data.
        *** Split revenue into Search and Detail for the target period. For Search, if Page 1 vs Page 2+ is available in finn_analysis_txt, report it; otherwise report only total Search.
        *** Explain the revenue change by RPOM and Traffic for both Search and Detail page. Do not compare between Page Types.
        *** Do not calculate the actual pageviews vs. forecasts directly referencing the data in forecast_traffic_data. Include both actual and percentage differences.
        *** Highlight which page type contributes most to revenue change, and mention "We now deep dive into this page type".
      ** Deep Dive for selected Page Type - refer to finn_analysis_txt.
        *** Refer to the "Metrics Bridge & Metrics Contribution" table, and report total revenue of that Device Type.
        *** Do not include placement-level breakdowns; skip the largest revenue callout.
      ** RPOM Analysis - refer to finn_analysis_txt
        *** For the selected Page Type + Device Type combo.
        *** Group into positive vs. negative contributors.`;

  const reportOutputBody = isSB ? sbReportOutputBody : spReportOutputBody;

  return `
${IMPORTANT_CONTEXT(metadata)}
${getSBGuards(isSB)}
${FORMAT_BLOCK(metadata)}
<holiday_list>${HOLIDAY_LIST}</holiday_list>
<finn_analysis_txt>${analysisData}</finn_analysis_txt>
<forecast_traffic_data>${forecastData}</forecast_traffic_data>
<input_context>
  * We are analyzing the ${product} (${
    isSB ? "Sponsored Brands" : "Sponsored Products"
  }) ${geoName} revenue data between ${metadata.targetDateStart} and ${
    metadata.targetDateEnd
  } (target), using ${metadata.baselineDateStart}~${
    metadata.baselineDateEnd
  } (baseline) as the baseline.
  * This analysis is exclusively for ${product}. Do NOT mention or reference any other ad product.
  * You will need three sources to build this report.
    ** 1. finn_analysis_txt that provides revenue data for different dimensions.
    ** 2. Pre-processed forecasted traffic data for each marketplace, which is provided in the forecast_traffic_data.
    ** 3. holiday_list with major holidays known to affect metrics.
</input_context>
${getBusinessFramework(product, geoName)}
<txt_data_context>
  The following describes how to interpret the finn_analysis_txt and similar tabular sources:
  * There are four tables:
    ** 1. Metrics: Metrics for target date.
    ** 2. Metrics Changes: actual variance between target and baseline periods.
    ** 3. Metrics Bridge: each cohort's revenue contribution to the total.
    ** 4. Metrics Contribution: each cohort's % contribution to the total.
  * Structure:
    ** The data may be represented as a list-of-lists structure.
    ** Row 0 (the first sub-list) = column headers.
    ** Column 0 in every subsequent row = metric name.
    ** The remaining cells in each row = metric values aligned to the headers.
  ${getPageGroupingRules(isSB)}
  * Parsing rules:
    ** Strip symbols (MM, %, +, $, commas) before doing math.
     - Example: "16,270.98MM" → 16270.98
     - Example: "+20.3%" → 0.203
    ** Interpret "MM" as millions, "K" as thousands, "B" as billions, and "%" as a fraction.
</txt_data_context>
${getSBOutputGuard(isSB)}
<forecast_traffic_data_context>
  Here is some information about the forecast_traffic_data:
  * Dimensional columns:
    ** geo: the region code (e.g., 'NA', 'EU','ROW'). Or worldwide aggregated ('WW'). Or the marketplace code (e.g., 'US').
    ** page_type:
      *** Two types: 'Search' and 'Detail'. Or the aggregated 'Search+Detail'.
      *** If not specified, default to 'Search+Detail'.
    ** device_type:
      *** Two types: 'Desktop' and 'Mobile'. Or the 'Desktop+Mobile'.
      *** Both page types are available on both device types.
      *** If not specified, default to 'Desktop+Mobile'.
    ** time_period: target time period.
  * Traffic Forecasts
    ** pageviews: Page views (aka Traffic)
    ** ro_forecasted_pageviews: Guidance forecast page views.
    ** op2_forecasted_pageviews: OP2 forecast page views.
    ** Use these four forecast values only when explicitly requested.
    ** ro_delta: pageviews - ro_forecasted_pageviews
    ** op_delta: pageviews - op2_forecasted_pageviews
    ** ro_variance: (pageviews - ro_forecasted_pageviews) / ro_forecasted_pageviews
    ** op_variance: (pageviews - op2_forecasted_pageviews) / op2_forecasted_pageviews
</forecast_traffic_data_context>
<holiday_data_context>
  Here is some information about the holiday_list:
  * Dimensional columns:
    ** geo: the marketplace code (e.g., 'US').
    ** holiday: name of major holiday
    ** start_date: date formatted value corresponding to when the major holiday starts
    ** end_date: date formatted value corresponding to when the major holiday ends
  * Usage guidance:
    ** Check if target or baseline period fall within ±7-day window around each holiday.
    ** For any metric date, if the geo matches and the date overlaps the window, mark the period as holiday-adjacent.
  * Call-out rule:
    ** If holiday-adjacent to either the target date range or baseline date range, add a note about it.
    ** Please callout if the target and baseline date range only cover part of the holiday date range. This will help us know whether we are comparing apples to apples.
</holiday_data_context>
<writing_guidance>
  Summarized best practices for style and clarity:
    ** Be concise: Remove redundant words, qualifiers (e.g., "really", "very"), and unnecessary modifiers. Each sentence should add value.
    ** Use active voice: Prefer "We reduced latency" over "Latency was reduced".
    ** Positive framing: Express outcomes directly (e.g., "Fixing this achieves OP2" vs. "If we don't fix this, we miss OP2").
    ** Clarity first Use simple, specific words. Replace vague or subjective terms ("strong", "significant", "remarkable") with data.
    ** Structure by paragraph: Each paragraph has one idea: start with topic, support with details, close with impact.
    ** Avoid weasel words: Do not use "might", "should", "often", "most". Replace with facts or omit.
    ** Minimize adjectives/adverbs: Use sparingly and only when supported by data (e.g., "Sales grew 450bps in Q4" instead of "Sales grew quickly").
    ** Formatting: Use headers and white space for readability. Acronyms should be spelled out once, then abbreviated. Keep length ≤ six pages excluding appendices.
    ** Neutral tone enforcement:
     - Avoid subjective or promotional terms. Replace with data.
     - Banned terms (case-insensitive, substring match): strong, strongest, significant, remarkable, dramatic, substantial, importance, impressive, exceptional.
    ** No tables anywhere in the response. If you find yourself creating columns or rows, rewrite the content as continuous prose before returning the answer.
</writing_guidance>
<actions>
  Analyze PoP Revenue on target period, compared to baseline period for ${geoName}.
</actions>
${HTML_OUTPUT_FORMATTING}
<report_output>
  This section defines the structure of executive summary. Please start with a header - "Executive Summary", also please follow the format guidance and writing_guidance to populate the narratives. Do not include any table markup; the entire section must be composed only of paragraphs with inline spans for color rules when needed.
  You will need to pull data from the finn_analysis_txt table using the Marketplace and marketplace id mapping in business_framework.
  It is important to note that the scope of the data in the finn_analysis_txt table is limited to the requested ${device} and whichever geos are present in the table.
  Be specific about the geo and the device when referring to certain metrics but try not to repeat the same too many times. (e.g. avoid using 'overall revenue', use '${geoName} ${device} revenue')
  When aggregating or interpreting the data, always apply the rules defined in txt_data_context:
  ${reportOutputBody}
</report_output>
`;
}

/**
 * Build the trend analysis prompt.
 *
 * @param trendData - Stringified trend data
 * @param metadata  - Analysis metadata
 * @param geoName   - Geo group name
 * @param device    - Device type
 */
export function buildTrendAnalysisPrompt(
  trendData: string,
  metadata: PromptMetadata,
  geoName: string,
  device: string
): string {
  const analysisType = metadata.comparisonMode.toLowerCase();
  const product = metadata.product;
  const isSB = product === "SB";
  const productLabel = isSB
    ? "Sponsored Brands (SB)"
    : "Sponsored Products (SP)";

  return `
${IMPORTANT_CONTEXT(metadata)}
${getSBGuards(isSB)}
${FORMAT_BLOCK(metadata)}
<product_context>
  This analysis is for ${productLabel} only. Do NOT mention or reference any other ad product.
  Always refer to the product as "${product}" or "${productLabel}" in the output.
</product_context>
<trend_data>${trendData}</trend_data>
<run_condition>
  This module must only run for Year-over-Year (YoY), Quarter-over-Quarter (QoQ), Month-over-Month (MoM), or Week-over-Week (WoW) requests.

  Validation Rules:
  * analysis_type == 'yoy': If baseline_date_start and target_date_start gap is NOT between 364 and 366 days; or baseline_date_end and target_date_end gap is NOT between 364 and 366 days - produce one line output - "Error processing YoY trend".
  * analysis_type == 'qoq': If baseline_date_start and target_date_start gap is NOT between 90 and 92 days; or baseline_date_end and target_date_end gap is NOT between 90 and 92 days - produce one line output - "Error processing QoQ trend".
  * analysis_type == 'mom': If baseline_date_start and target_date_start gap is NOT between 28 and 31 days; or baseline_date_end and target_date_end gap is NOT between 28 and 31 days - produce one line output - "Error processing MoM trend".
  * analysis_type == 'wow': If baseline_date_start and target_date_start gap is NOT 7 days; or baseline_date_end and target_date_end gap is NOT 7 days - produce one line output - "Error processing WoW trend".

  If one of the above validations pass, proceed with the following analysis.
</run_condition>
<input_data_context>
  This section provides schema details in trend_data. It is a pre-processed and multi-granular ${product} revenue trend data for the selected ${geoName} and ${device}.
  * Product: ${productLabel}
  * Analysis Type: ${analysisType}
  * Dimensions:
    ** geo: determined by the ${geoName} input from user. It could be marketplace code (e.g. 'US'), region code (e.g., 'NA', 'EU','ROW'), or world-wide aggregated ('WW').
    ** date_type: defines the data granularity.
      *** 'target_date_range' is the target period user defined.
      *** 'day' -> the metrics for a specific day
      *** 'week' -> the metrics for a specific week; date_value_baseline & date_value_target == the end of the week (Saturday).
      *** 'month' -> the metrics for a specific month; date_value_baseline & date_value_target == the last day of the month.
      *** 'quarter' -> the metrics for a specific quarter; date_value_baseline & date_value_target == the last day of the quarter.
      *** 'year' -> the metrics for that year; date_value_baseline & date_value_target == the last day of the year. If the year is incomplete, values represent year-to-date (partial data).
    ** analysis_type: the type of analysis user requested. There will only be one value each time the trend_data is populated.
      *** 'yoy' -> Year-over-Year (YoY) analysis. The gap between target period and baseline period is between 364 and 366 days.
      *** 'qoq' -> Quarter-over-Quarter (QoQ) analysis. The gap between target period and baseline period is between 90 and 92 days.
      *** 'mom' -> Month-over-Month (MoM) analysis. The gap between target period and baseline period is between 28 and 31 days.
      *** 'wow' -> Week-over-Week (WoW) analysis. The gap between target period and baseline period is 7 days.
    ** date_value_baseline: defines the reference period used for comparison.
    ** date_value_target: defines period being analyzed.
  * Metrics:
    ** Actual Revenue:
      *** rev_baseline_usd: aggregated actual ${product} revenue for baseline period.
      *** rev_target_usd: aggregated actual ${product} revenue for target period.
    ** Revenue Forecasts: Use these two forecast values only when explicitly requested.
      *** ro_rev_target_usd: guidance revenue forecast for the target date range.
      *** op2_rev_target_usd: OP2 revenue forecast for the target date range.
    ** Revenue Growth/Decrease:
      *** actual_var: this is the percentage of actual revenue variance of date_value_target vs. the date_value_baseline.
    ** Actual vs. Forecasts: do not self-calculate; use provided fields in trend_data. Include both actual and percentage variances.
      *** ro_delta: the positive/negative delta between actual revenue (rev_target_usd) and the guidance forecast (ro_rev_target_usd)
      *** op2_delta: the positive/negative delta between actual revenue (rev_target_usd) and the OP2 forecast (op2_rev_target_usd)
      *** ro_var: the % of variance between actual revenue (rev_target_usd) and the guidance forecast (ro_rev_target_usd)
      *** op2_var: the % of variance between actual revenue (rev_target_usd) and the OP2 forecast (op2_rev_target_usd)
</input_data_context>
${HTML_OUTPUT_FORMATTING}

<scenario name="Year-over-Year (YoY)">
  <trend_guidance>
    This section provides guidance for analyzing year-over-year (YoY) revenue growth by comparing the user-specified ${
      metadata.targetDateStart
    } and ${metadata.targetDateEnd} with the corresponding previous period ${
    metadata.baselineDateStart
  } and ${
    metadata.baselineDateEnd
  }. To determine whether the requested YoY trend is expected, trend_data supplies multiple recent YoY comparisons (e.g., the latest weeks or months) to help identify any deviations.
    * Data Source: You will need the pre-processed trend_data to perform this analysis. Refer to input_data_context for schema details.
    * YoY calculation method:
      ** When calculating YoY revenue, always calculate YoY revenue by comparing later year's data (e.g. 2025) to the corresponding prior year's data (e.g. 2024). Ensure the comparison is made for the same date_type and same geo.
        *** Example (day): 7/13/2025 vs. 7/14/2024 (gap 364–366 days)
        *** Example (week): week of 8/9/2025 vs. week of 8/10/2024 (date_value_baseline & date_value_target are dated Saturday endpoints)
        *** Example (month): month of 7/31/2025 vs. month of 7/31/2024 (date_value_baseline & date_value_target are dated the last of a month)
        *** Example (quarter): 9/30/2025 vs. 9/30/2024 (date_value_baseline & date_value_target are dated the last of a quarter)
      ** Make sure the YoY calculation is based on the percentage change: (later year's metric / earlier year's metric) − 1, rather than simply subtracting the earlier year's metric from the later year's metric.
    * Target vs. Benchmark: Use target period YoY vs. other recent benchmark period YoY (weekly, monthly, or quarterly as applicable).
      ** If date_type == 'target_date_range': this row is the target period YoY
      ** If date_type != 'target_date_range': these rows are the benchmark period YoY
      ** Gap Flags: Calculate the absolute gap (|target period YoY – benchmark period YoY|).
        *** < 1% - state that it is consistent.
        *** 3%–5% - don't need to state specifically; described as "an improvement/decline of |target date % YoY – benchmark date % YoY| compared with past weeks."
        *** >5% - described as "an accelerating/decelerating growth compared with past weeks."
    * YoY Key Insights:
      ** 1. Overview: Write one line that describes the ${
        metadata.targetDateStart
      }~${
    metadata.targetDateEnd
  }'s YoY for that ${geoName} and ${device}. Template - "During X date to Y date, [geo] revenue increased by +X% YoY."
      ** 2. Compared with guidance: for the target period, write one line to report compare its actual revenue (rev_target_usd) against guidance forecast (ro_rev_target_usd) and OP2 forecast (op2_rev_target_usd). Include both actual and percentage differences (ro_var and op2_var). Do not include any interpretation of performance strength when reporting #2.
      ** 3. Compared with benchmark trend:
        *** Calculate the YoY trend for each benchmark
        *** if target period is a 'day' or a 'week', compare the trailing 4 benchmark weeks' YoY trend against the target period YoY.
        *** If target period is a 'month', compare the trailing 4 benchmark months' YoY trend against the target period YoY.
        *** If target period is a 'quarter', compare the latest benchmark quarters' YoY trend against the target period YoY.
        *** When checking, refer to the "Gap Flags" guidance above. Always report the YoY values first, then the benchmark, then the interpretation.
  </trend_guidance>
  <actions>
    Analyze the ${product} YoY Revenue change for the target dates.
    Parameters:
      product: ${product},
      target_date_start: ${metadata.targetDateStart},
      target_date_end: ${metadata.targetDateEnd},
      baseline_date_start: ${metadata.baselineDateStart},
      baseline_date_end: ${metadata.baselineDateEnd},
      geo: ${geoName},
      device_type: ${device},
      analysis_type: ${analysisType}
  </actions>
  <output>
    Please include the below analyses into one paragraph:
      ** Report YoY analysis from "YoY Key Insights" #1 in trend_guidance.
      ** Report guidance analysis from "YoY Key Insights" #2 in trend_guidance.
      ** Report the weekly analysis from "YoY Key Insights" #3 in trend_guidance.
  </output>
  <validation>
    * If there's data missing, do not generate the report. This is important.
    * Double check the following absolute numbers:
      ** The actual Revenue (rev_target_usd), and forecasted Revenue (ro_rev_target_usd, op2_rev_target_usd) for each row
      ** The YoY percentage change for each row: (later year / earlier year) − 1
    * Double check the target and/or baseline date falls within a holiday and call out in the output if true.
  </validation>
</scenario>

<scenario name="Quarter-over-Quarter (QoQ)">
  <trend_guidance>
    This section provides guidance for analyzing Quarter-over-Quarter (QoQ) revenue differences by comparing the user-specified ${
      metadata.targetDateStart
    } and ${metadata.targetDateEnd} with the corresponding previous period ${
    metadata.baselineDateStart
  } and ${
    metadata.baselineDateEnd
  }. To determine whether the requested QoQ trend is expected, trend_data supplies multiple recent QoQ comparisons (e.g., the latest weeks or months) to help identify any deviations.
    * Data Source: You will need the pre-processed trend_data to perform this analysis. Refer to input_data_context for schema details.
    * QoQ calculation method:
      ** When calculating QoQ revenue, always calculate QoQ revenue by comparing later quarter's data (e.g. Q2 2025) to the corresponding prior quarter's data (e.g. Q1 2025). Ensure the comparison is made for the same date_type and same geo.
        *** Example (day): 6/1/2025 vs. 3/1/2025 (gap 92–96 days)
        *** Example (week): week of 8/9/2025 vs. week of 5/9/2025 (date_value_baseline & date_value_target are dated Saturday endpoints)
        *** Example (month): month of 7/31/2025 vs. month of 4/30/2025 (date_value_baseline & date_value_target are dated the last of a month)
        *** Example (quarter): 9/30/2025 vs. 6/30/2025 (date_value_baseline & date_value_target are dated the last of a quarter)
      ** Make sure the QoQ calculation is based on the percentage change: (later quarter's metric / earlier quarter's metric) − 1, rather than simply subtracting the earlier quarter's metric from the later quarter's metric.
    * Target vs. Benchmark: Use target period QoQ vs. other recent benchmark period QoQ (weekly, monthly, or quarterly as applicable).
      ** If date_type == 'target_date_range': this row is the target period QoQ
      ** If date_type != 'target_date_range': these rows are the benchmark period QoQ
      ** Gap Flags: Calculate the absolute gap (|target period QoQ – benchmark period QoQ|).
        *** < 1% - state that it is consistent.
        *** 3%–5% - don't need to state specifically; described as "an improvement/decline of |target date % QoQ – benchmark date % QoQ| compared with past weeks."
        *** >5% - described as "an accelerating/decelerating growth compared with past weeks."
    * QoQ Key Insights:
      ** 1. Overview: Write one line that describes the ${
        metadata.targetDateStart
      }~${
    metadata.targetDateEnd
  }'s QoQ for that ${geoName} and ${device}. Template - "During X date to Y date, [geo] revenue increased by +X% QoQ."
      ** 2. Compared with guidance: for the target period, write one line to report compare its actual revenue (rev_target_usd) against guidance forecast (ro_rev_target_usd) and OP2 forecast (op2_rev_target_usd). Include both actual and percentage differences (ro_var and op2_var). Do not include any interpretation of performance strength when reporting #2.
      ** 3. Compared with benchmark trend:
        *** Calculate the QoQ trend for each benchmark
        *** if target period is a 'day' or a 'week', compare the trailing 4 benchmark weeks' QoQ trend against the target period QoQ.
        *** If target period is a 'month', compare the trailing 4 benchmark months' QoQ trend against the target period QoQ.
        *** If target period is a 'quarter', compare the latest benchmark quarters' QoQ trend against the target period QoQ.
        *** When checking, refer to the "Gap Flags" guidance above. Always report the QoQ values first, then the benchmark, then the interpretation.
  </trend_guidance>
  <actions>
    Analyze the ${product} QoQ Revenue change for the target dates.
    Parameters:
      product: ${product},
      target_date_start: ${metadata.targetDateStart},
      target_date_end: ${metadata.targetDateEnd},
      baseline_date_start: ${metadata.baselineDateStart},
      baseline_date_end: ${metadata.baselineDateEnd},
      geo: ${geoName},
      device_type: ${device},
      analysis_type: ${analysisType}
  </actions>
  <output>
    Please include the below analyses into one paragraph:
      ** Report QoQ analysis from "QoQ Key Insights" #1 in trend_guidance.
      ** Report guidance analysis from "QoQ Key Insights" #2 in trend_guidance.
      ** Report the weekly analysis from "QoQ Key Insights" #3 in trend_guidance.
  </output>
  <validation>
    * If there's data missing, do not generate the report. This is important.
    * Double check the following absolute numbers:
      ** The actual Revenue (rev_target_usd), and forecasted Revenue (ro_rev_target_usd, op2_rev_target_usd) for each row
      ** The QoQ percentage change for each row: (later quarter / earlier quarter) − 1
    * Double check the target and/or baseline date falls within a holiday and call out in the output if true.
  </validation>
</scenario>

<scenario name="Month-over-Month (MoM)">
  <trend_guidance>
    This section provides guidance for analyzing Month-over-Month (MoM) revenue differences by comparing the user-specified ${
      metadata.targetDateStart
    } and ${metadata.targetDateEnd} with the corresponding previous period ${
    metadata.baselineDateStart
  } and ${
    metadata.baselineDateEnd
  }. To determine whether the requested MoM trend is expected, trend_data supplies multiple recent MoM comparisons (e.g., the latest weeks or months) to help identify any deviations.
    * Data Source: You will need the pre-processed trend_data to perform this analysis. Refer to input_data_context for schema details.
    * MoM calculation method:
      ** When calculating MoM revenue, always calculate MoM revenue by comparing later month's data (e.g. June 2025) to the corresponding prior month's data (e.g. May 2025). Ensure the comparison is made for the same date_type and same geo.
        *** Example (day): 6/1/2025 vs. 5/1/2025 (gap 28-31 days)
        *** Example (week): week of 8/9/2025 vs. week of 7/9/2025 (date_value_baseline & date_value_target are dated Saturday endpoints)
        *** Example (month): month of 7/31/2025 vs. month of 6/30/2025 (date_value_baseline & date_value_target are dated the last of a month)
      ** Make sure the MoM calculation is based on the percentage change: (later month's metric / earlier month's metric) − 1, rather than simply subtracting the earlier month's metric from the later month's metric.
    * Target vs. Benchmark: Use target period MoM vs. other recent benchmark period MoM (weekly, monthly as applicable).
      ** If date_type == 'target_date_range': this row is the target period MoM
      ** If date_type != 'target_date_range': these rows are the benchmark period MoM
      ** Gap Flags: Calculate the absolute gap (|target period MoM – benchmark period MoM|).
        *** < 1% - state that it is consistent.
        *** 3%–5% - don't need to state specifically; described as "an improvement/decline of |target date % MoM – benchmark date % MoM| compared with past weeks."
        *** >5% - described as "an accelerating/decelerating growth compared with past weeks."
    * MoM Key Insights:
      ** 1. Overview: Write one line that describes the ${
        metadata.targetDateStart
      }~${
    metadata.targetDateEnd
  }'s MoM for that ${geoName} and ${device}. Template - "During X date to Y date, [geo] revenue increased by +X% MoM."
      ** 2. Compared with guidance: for the target period, write one line to report compare its actual revenue (rev_target_usd) against guidance forecast (ro_rev_target_usd) and OP2 forecast (op2_rev_target_usd). Include both actual and percentage differences (ro_var and op2_var). Do not include any interpretation of performance strength when reporting #2.
      ** 3. Compared with benchmark trend:
        *** Calculate the MoM trend for each benchmark
        *** if target period is a 'day' or a 'week', compare the trailing 4 benchmark weeks' MoM trend against the target period MoM.
        *** If target period is a 'month', compare the trailing 4 benchmark months' MoM trend against the target period MoM.
        *** When checking, refer to the "Gap Flags" guidance above. Always report the MoM values first, then the benchmark, then the interpretation.
  </trend_guidance>
  <actions>
    Analyze the ${product} MoM Revenue change for the target dates.
    Parameters:
      product: ${product},
      target_date_start: ${metadata.targetDateStart},
      target_date_end: ${metadata.targetDateEnd},
      baseline_date_start: ${metadata.baselineDateStart},
      baseline_date_end: ${metadata.baselineDateEnd},
      geo: ${geoName},
      device_type: ${device},
      analysis_type: ${analysisType}
  </actions>
  <output>
    Please include the below analyses into one paragraph:
      ** Report MoM analysis from "MoM Key Insights" #1 in trend_guidance.
      ** Report guidance analysis from "MoM Key Insights" #2 in trend_guidance.
      ** Report the weekly analysis from "MoM Key Insights" #3 in trend_guidance.
  </output>
  <validation>
    * If there's data missing, do not generate the report. This is important.
    * Double check the following absolute numbers:
      ** The actual Revenue (rev_target_usd), and forecasted Revenue (ro_rev_target_usd, op2_rev_target_usd) for each row
      ** The MoM percentage change for each row: (later month / earlier month) − 1
    * Double check the target and/or baseline date falls within a holiday and call out in the output if true.
  </validation>
</scenario>

<scenario name="Week-over-Week (WoW)">
  <trend_guidance>
    This section provides guidance for analyzing Week-over-Week (WoW) revenue differences by comparing the user-specified ${
      metadata.targetDateStart
    } and ${metadata.targetDateEnd} with the corresponding previous period ${
    metadata.baselineDateStart
  } and ${
    metadata.baselineDateEnd
  }. To determine whether the requested WoW trend is expected, trend_data supplies multiple recent WoW comparisons (e.g., the latest weeks) to help identify any deviations.
    * Data Source: You will need the pre-processed trend_data to perform this analysis. Refer to input_data_context for schema details.
    * WoW calculation method:
      ** When calculating WoW revenue, always calculate WoW revenue by comparing later week's data (e.g. 7/5/2025) to the corresponding prior week's data (e.g. 6/28/2025). Ensure the comparison is made for the same date_type and same geo.
        *** Example (day): 6/29/2025 vs. 6/22/2025 (gap 7 days)
        *** Example (week): week of 8/9/2025 vs. week of 8/2/2025 (date_value_baseline & date_value_target are dated Saturday endpoints)
      ** Make sure the WoW calculation is based on the percentage change: (later week's metric / earlier week's metric) − 1, rather than simply subtracting the earlier week's metric from the later week's metric.
    * Target vs. Benchmark: Use target period WoW vs. other recent benchmark period WoW (weekly as applicable).
      ** If date_type == 'target_date_range': this row is the target period WoW
      ** If date_type != 'target_date_range': these rows are the benchmark period WoW
      ** Gap Flags: Calculate the absolute gap (|target period WoW – benchmark period WoW|).
        *** < 1% - state that it is consistent.
        *** 3%–5% - don't need to state specifically; described as "an improvement/decline of |target date % WoW – benchmark date % WoW| compared with past weeks."
        *** >5% - described as "an accelerating/decelerating growth compared with past weeks."
    * WoW Key Insights:
      ** 1. Overview: Write one line that describes the ${
        metadata.targetDateStart
      }~${
    metadata.targetDateEnd
  }'s WoW for that ${geoName} and ${device}. Template - "During X date to Y date, [geo] revenue increased by +X% WoW."
      ** 2. Compared with guidance: for the target period, write one line to report compare its actual revenue (rev_target_usd) against guidance forecast (ro_rev_target_usd) and OP2 forecast (op2_rev_target_usd). Include both actual and percentage differences (ro_var and op2_var). Do not include any interpretation of performance strength when reporting #2.
      ** 3. Compared with benchmark trend:
        *** Calculate the WoW trend for each benchmark
        *** if target period is a 'day' or a 'week', compare the trailing 4 benchmark weeks' WoW trend against the target period WoW.
        *** When checking, refer to the "Gap Flags" guidance above. Always report the WoW values first, then the benchmark, then the interpretation.
  </trend_guidance>
  <actions>
    Analyze the ${product} WoW Revenue change for the target dates.
    Parameters:
      product: ${product},
      target_date_start: ${metadata.targetDateStart},
      target_date_end: ${metadata.targetDateEnd},
      baseline_date_start: ${metadata.baselineDateStart},
      baseline_date_end: ${metadata.baselineDateEnd},
      geo: ${geoName},
      device_type: ${device},
      analysis_type: ${analysisType}
  </actions>
  <output>
    Please include the below analyses into one paragraph:
      ** Report WoW analysis from "WoW Key Insights" #1 in trend_guidance.
      ** Report guidance analysis from "WoW Key Insights" #2 in trend_guidance.
      ** Report the weekly analysis from "WoW Key Insights" #3 in trend_guidance.
  </output>
  <validation>
    * If there's data missing, do not generate the report. This is important.
    * Double check the following absolute numbers:
      ** The actual Revenue (rev_target_usd), and forecasted Revenue (ro_rev_target_usd, op2_rev_target_usd) for each row
      ** The WoW percentage change for each row: (later week / earlier week) − 1
    * Double check the target and/or baseline date falls within a holiday and call out in the output if true.
  </validation>
</scenario>
`;
}

/**
 * Build both prompts for the SPB Analytics AI summary.
 * Returns [marketplacePrompt, trendPrompt].
 */
export function buildSPBPrompts(
  data: any,
  filters: Record<string, any>
): string[] {
  const meta: PromptMetadata = {
    product: filters.product ?? "SP",
    targetDateStart: filters.targetStartDate ?? "",
    targetDateEnd: filters.targetEndDate ?? "",
    baselineDateStart: filters.baselineStartDate ?? "",
    baselineDateEnd: filters.baselineEndDate ?? "",
    comparisonMode: filters.comparisonMode ?? "YoY",
  };

  const geoName = data?.geoGroupName ?? "WW";
  const device = filters.device ?? "Desktop-Mobile";
  const analysisData = data?.analysisDataStr ?? "";
  const forecastData = data?.forecastDataStr ?? "";
  const trendData = data?.trendDataStr ?? "";

  return [
    buildMarketplaceAnalysisPrompt(
      analysisData,
      meta,
      forecastData,
      geoName,
      device
    ),
    buildTrendAnalysisPrompt(trendData, meta, geoName, device),
  ];
}
